import React from 'react';
import { ScoreboardState } from '@/services/scoreboard';

function deriveShortLocal(name?: string) {
  if (!name) return '---';
  const s = String(name).trim().toUpperCase();
  if (!s) return '---';
  const map: Record<string, string> = {
    'Á':'A','Ä':'A','Å':'A','Â':'A','À':'A',
    'Č':'C','Ć':'C','Ç':'C',
    'Ď':'D',
    'É':'E','Ě':'E','È':'E','Ë':'E','Ê':'E',
    'Í':'I','Ì':'I','Ï':'I','Î':'I',
    'Ň':'N','Ń':'N',
    'Ó':'O','Ö':'O','Ô':'O','Ò':'O',
    'Ř':'R',
    'Š':'S','Ś':'S',
    'Ť':'T',
    'Ú':'U','Ů':'U','Ù':'U','Ü':'U','Û':'U',
    'Ý':'Y',
    'Ž':'Z',
  };
  let out = '';
  for (const ch of s) {
    const c = map[ch] || ch;
    if (c >= 'A' && c <= 'Z') {
      out += c;
      if (out.length === 3) break;
    }
  }
  while (out.length < 3) out += '-';
  return out;
}

function shade(hex: string, percent: number) {
  try {
    const n = hex.replace('#','');
    const num = parseInt(n.length === 3 ? n.split('').map((c)=>c+c).join('') : n, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    r = Math.min(255, Math.max(0, Math.round(r + (percent/100)*255)));
    g = Math.min(255, Math.max(0, Math.round(g + (percent/100)*255)));
    b = Math.min(255, Math.max(0, Math.round(b + (percent/100)*255)));
    return `#${[r,g,b].map(v=>v.toString(16).padStart(2,'0')).join('')}`;
  } catch {
    return hex;
  }
}

const styleBlock = `
.pill-wrapper { width: max-content; margin: 24px auto; display: flex; flex-direction: column; align-items: center; gap: 18px; }
.scoreboard { display: flex; justify-content: space-between; align-items: center; background: rgba(0,0,0,0.75); color: #ffffff; padding: 18px 28px; font-size: 32px; font-weight: 700; border-radius: 14px; width: min(90vw, 900px); margin: 24px auto; gap: 20px; box-shadow: 0 8px 24px rgba(0,0,0,0.35); backdrop-filter: blur(6px); border: 1px solid rgba(255,255,255,0.15); }
.scoreboard.pill { background: var(--pill-bg, #f8fafc); color: var(--pill-text, #0f172a); border: 1px solid #e5e7eb; box-shadow: 0 10px 30px rgba(2,6,23,0.18); border-radius: 999px; padding: 4px 6px; width: max-content; margin: 0 auto; gap: 6px; backdrop-filter: none; font-size: 15px; transform: scale(var(--pill-scale, 1.7)); transform-origin: center; will-change: transform; }
.scoreboard.pill .seg { display: flex; align-items: center; justify-content: center; height: 36px; }
.scoreboard.pill .seg.timer { font-variant-numeric: tabular-nums; font-weight: 800; background: linear-gradient(180deg, #eef2f7 0%, #e2e8f0 100%); padding: 0 12px 0 8px; border-radius: 999px; font-size: 15px; color: #0f172a; }
.scoreboard.pill .seg.team { padding: 0 10px; border-radius: 10px; font-weight: 800; letter-spacing: 0.5px; min-width: 46px; text-transform: uppercase; position: relative; overflow: visible; }
.scoreboard.pill .seg.team.home { background: linear-gradient(90deg, var(--home-dark), var(--home-light)); color: var(--home-text, #ffffff); }
.scoreboard.pill .seg.team.away { background: linear-gradient(90deg, var(--away-dark), var(--away-light)); color: var(--away-text, #ffffff); }
.scoreboard.pill .seg.score { background: linear-gradient(180deg, #ffffff 0%, #f3f4f6 100%); border: 1px solid #e5e7eb; border-radius: 10px; padding: 0 10px; font-weight: 800; color: #0f172a; min-width: 58px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.9); font-size: 15px; }
.scoreboard.pill .divider { width: 2px; height: 14px; background: rgba(15,23,42,0.35); border-radius: 1px; align-self: center; }
.scoreboard.pill .team .logo { width: 24px; height: 24px; object-fit: contain; margin-right: 6px; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.2)); }
.scoreboard.pill .team.away .logo { margin-left: 6px; margin-right: 0; }
.fouls-bar { display: flex; align-items: center; justify-content: space-between; gap: 32px; width: 100%; padding: 18px 18px 0; transform: scale(var(--pill-scale, 1.7)); transform-origin: center; }
.fouls-group { display: flex; gap: 6px; }
.foul-dot { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.9); background: rgba(255,255,255,0.15); box-shadow: 0 1px 2px rgba(0,0,0,0.25); }
.foul-dot.active { background: #ff1f1f; border-color: #ff1f1f; }
@media (max-width: 640px) {
  .pill-wrapper { margin: 24px auto 16px; gap: 14px; }
  .scoreboard.pill { padding: 6px 8px; transform: scale(1.0); transform-origin: center; }
  .pill .seg.timer, .pill .seg.score { padding: 4px 10px; }
  .fouls-bar { gap: 20px; padding: 12px 12px 0; transform: scale(1.0); }
}
`;

const MyClubOverlay: React.FC<{ state: ScoreboardState }> = ({ state }) => {
  const theme = state.theme || 'pill';
  const isFlipped = !!state.sidesFlipped;
  const left = {
    short: (isFlipped ? state.awayShort : state.homeShort) || deriveShortLocal(isFlipped ? state.awayName : state.homeName),
    logo: isFlipped ? state.awayLogo : state.homeLogo,
    color: (isFlipped ? state.secondaryColor : state.primaryColor) || '#1e3a8a',
    score: isFlipped ? state.awayScore : state.homeScore,
    fouls: Math.max(0, Math.min(5, isFlipped ? (state.awayFouls || 0) : (state.homeFouls || 0))),
  };
  const right = {
    short: (!isFlipped ? state.awayShort : state.homeShort) || deriveShortLocal(!isFlipped ? state.awayName : state.homeName),
    logo: !isFlipped ? state.awayLogo : state.homeLogo,
    color: (!isFlipped ? state.secondaryColor : state.primaryColor) || '#2563eb',
    score: !isFlipped ? state.awayScore : state.homeScore,
    fouls: Math.max(0, Math.min(5, !isFlipped ? (state.awayFouls || 0) : (state.homeFouls || 0))),
  };
  const timer = state.timer || '00:00';

  const cssVars: React.CSSProperties = {
    // @ts-ignore
    '--home-dark': left.color,
    // @ts-ignore
    '--home-light': shade(left.color, 20),
    // @ts-ignore
    '--away-dark': right.color,
    // @ts-ignore
    '--away-light': shade(right.color, 20),
    // @ts-ignore
    '--home-text': (state as any).homeTextColor || '#ffffff',
    // @ts-ignore
    '--away-text': (state as any).awayTextColor || '#ffffff',
  } as any;

  if (theme !== 'pill') {
    return (
      <>
        <style>{styleBlock}</style>
        <div className="pill-wrapper" style={cssVars as any}>
          <div className="scoreboard pill">
            <div className="seg timer"><span>{timer}</span></div>
            <div className="seg team home"><img className="logo" alt="" src={left.logo || ''} />
              <span>{left.short}</span>
            </div>
            <span className="divider" aria-hidden="true"></span>
            <div className="seg score"><span>{left.score}-{right.score}</span></div>
            <span className="divider" aria-hidden="true"></span>
            <div className="seg team away"><span>{right.short}</span>
              <img className="logo" alt="" src={right.logo || ''} />
            </div>
          </div>
          <div className="fouls-bar">
            <div className="fouls-group">
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className={`foul-dot${i < left.fouls ? ' active' : ''}`}></span>
              ))}
            </div>
            <div className="fouls-group">
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className={`foul-dot${i < right.fouls ? ' active' : ''}`}></span>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styleBlock}</style>
      <div className="pill-wrapper" style={cssVars as any}>
        <div className="scoreboard pill">
          <div className="seg timer"><span>{timer}</span></div>
          <div className="seg team home"><img className="logo" alt="" src={left.logo || ''} />
            <span>{left.short}</span>
          </div>
          <span className="divider" aria-hidden="true"></span>
          <div className="seg score"><span>{left.score}-{right.score}</span></div>
          <span className="divider" aria-hidden="true"></span>
          <div className="seg team away"><span>{right.short}</span>
            <img className="logo" alt="" src={right.logo || ''} />
          </div>
        </div>
        <div className="fouls-bar">
          <div className="fouls-group">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className={`foul-dot${i < left.fouls ? ' active' : ''}`}></span>
            ))}
          </div>
          <div className="fouls-group">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className={`foul-dot${i < right.fouls ? ' active' : ''}`}></span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyClubOverlay;
